import { useEffect, useState } from 'react'
import LoginErrorMessage from '../../components/errors/LoginErrorMessage'
import { NavLink, useNavigate } from 'react-router'
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth'
import { firebaseApp } from '../../main'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/ReduxStore.ts'
import { useGetUserDetailsQuery } from '../../features/SpeechAgendaApiSlice.ts'
import ForgotPasswordPopUp from '../../components/modals/ForgotPasswordPopUp.tsx'
import { SignInWithGoogle } from '../../utils/FirebaseUtils.ts'
import SignInWithLinkModal from '../../components/modals/SignInWithLinkModal.tsx'
import logo from '../../assets/logo.svg'

export default function Login() {
  const navigate = useNavigate()
  const auth = getAuth(firebaseApp)
  const user = useSelector((state: RootState) => state.firebaseAuth.user)
  const [loginError, setLoginError] = useState<boolean>(false)
  const [forgotPasswordPopUpOpen, setForgotPasswordPopUpOpen] = useState(false)
  const [passwordlessPopUpOpen, setPasswordlessPopUpOpen] = useState(false)

  const { data, isFetching, isLoading, isError } = useGetUserDetailsQuery(
    undefined,
    {
      skip: !user,
    }
  )

  useEffect(() => {
    if (user && data && !isFetching && !isLoading && !isError) {
      // A user with no clubs has nothing to land on — send them to browse the
      // existing clubs rather than assume they want to register one.
      if (data.clubs.length === 0) {
        navigate('/clubs')
        return
      }
      const currentClubId = data.clubs[0].id.toString()
      navigate(`/club/${currentClubId}`)
    }
  }, [user, navigate, data, isFetching, isLoading, isError])

  // Handle form submission
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)
    const email = formData.get('username') as string
    const password = formData.get('password') as string

    try {
      await signInWithEmailAndPassword(auth, email, password)
    } catch {
      setLoginError(true)
    }
  }

  const handleSignInWithGoogle = async () => {
    await SignInWithGoogle()
  }

  const handleResetPassword = async () => {
    setForgotPasswordPopUpOpen(true)
  }

  return (
    <div className="flex min-h-full flex-col justify-center px-6 py-12 lg:px-8">
      <img
        src="https://dfc6zulft4rm9.cloudfront.net/images/landing-with-overlay.jpg"
        alt=""
        className="absolute inset-0 -z-10 h-full w-full object-cover opacity-5"
      />
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <img
          className="mx-auto h-10 w-auto"
          style={{ width: '60px', height: '60px' }}
          src={logo}
          alt="Speech Agenda"
        />
        <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
          Sign in to your account
        </h2>
      </div>
      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <form className="space-y-6" onSubmit={handleFormSubmit}>
          <div>
            <label
              htmlFor="username"
              className="block text-sm font-medium leading-6 text-gray-900">
              Email address
            </label>
            <div className="mt-2">
              <input
                id="username"
                name="username"
                type="email"
                autoComplete="email"
                required
                onChange={() => setLoginError(false)}
                onFocus={() => setLoginError(false)}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label
                htmlFor="password"
                className="block text-sm font-medium leading-6 text-gray-900">
                Password
              </label>
              <div className="text-sm">
                <button
                  onClick={handleResetPassword}
                  type="button"
                  className="font-semibold text-indigo-600 hover:text-indigo-500">
                  Forgot password?
                </button>
              </div>
            </div>
            <div className="mt-2">
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                onChange={() => setLoginError(false)}
                onFocus={() => setLoginError(false)}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
          {/* Error message */}
          {loginError && <LoginErrorMessage />}
          <div>
            <button
              type="submit"
              className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              Sign in
            </button>
          </div>
          <div className="mt-10">
            <div className="relative">
              <div
                className="absolute inset-0 flex items-center"
                aria-hidden="true">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm font-medium leading-6">
                <span className="px-6 text-gray-900">Or continue with</span>
              </div>
            </div>
            <div className="mt-6 w-full">
              <button
                onClick={handleSignInWithGoogle}
                className="flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:ring-transparent">
                <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                    fill="#EA4335"
                  />
                  <path
                    d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                    fill="#4285F4"
                  />
                  <path
                    d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
                    fill="#34A853"
                  />
                </svg>
                <span className="text-sm font-semibold leading-6">Google</span>
              </button>
            </div>
            <div className="mt-6 w-full">
              <button
                onClick={() => {
                  setPasswordlessPopUpOpen(true)
                }}
                className="flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-500 transition-colors duration-200">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-5 w-5">
                  <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
                  <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
                </svg>
                <span className="text-sm font-semibold leading-6">
                  Continue without password
                </span>
              </button>
            </div>
          </div>
        </form>
      </div>
      <div className="flex items-center justify-center mt-6">
        <div className="text-sm/6 flex items-center justify-center gap-1">
          <span className="text-gray-600">Don't have an account?</span>
          <NavLink
            to="/sign-up"
            className="font-semibold text-indigo-600 hover:text-indigo-500">
            Create an account
          </NavLink>
        </div>
      </div>
      <div className="flex items-center justify-center mt-6">
        <div className="text-sm/6 flex items-center justify-center gap-1">
          <NavLink
            to="/"
            className="font-semibold text-indigo-600 hover:text-indigo-500">
            Go back to the homepage
          </NavLink>
        </div>
      </div>
      <div className="flex items-center justify-center mt-6">
        <div className="text-sm/6 flex items-center justify-center gap-1">
          <NavLink
            to="/clubs"
            className="font-semibold text-indigo-600 hover:text-indigo-500">
            Explore Available Clubs on Speech Agenda
          </NavLink>
        </div>
      </div>
      <ForgotPasswordPopUp
        isOpen={forgotPasswordPopUpOpen}
        onClose={() => {
          setForgotPasswordPopUpOpen(false)
        }}
      />
      <SignInWithLinkModal
        isOpen={passwordlessPopUpOpen}
        onClose={() => {
          setPasswordlessPopUpOpen(false)
        }}
      />
    </div>
  )
}
